import axios from "axios";
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import {
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  Stack,
  Tooltip,
  styled,
  useTheme,
  Fab,
  Zoom,
  LinearProgress,
  Alert,
  Collapse,
  FormHelperText,
  ListItemIcon,
  Menu,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import three_dots from "../../../assets/img/tree_points.svg";

import {
  Settings as SettingsIcon,
  Star as StarIcon,
  Language as LanguageIcon,
  EmojiEvents as CertificateIcon,
  School as EducationIcon,
  Work as ExperienceIcon,
  Code as ProjectIcon,
  Palette as HobbyIcon,
  EmojiEvents as AwardIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Visibility as PreviewIcon,
  CloudDownload as DownloadIcon,
  AutoFixHigh as AutoFixIcon,
  Help as HelpIcon,
  KeyboardArrowUp as UpIcon,
  Download,
  Preview,
  Email,
  Warning,
} from "@mui/icons-material";
import useFetchUser from "hooks/useFetchUser";
import { useNavigate } from "react-router-dom";
import colors from "colors/color";
import { OutlinedButton } from "components/Buttons/Buttons";
import useUserId from "hooks/useUserId2";
import SmartHireLoader from "components/Loading/SmartHireLoader";
import NewAppBar from "components/Navbars/AppBar";
import { useSnackbar } from "hooks/useSbackbar";
import { useTranslation } from "react-i18next";
import { NewCancelButton } from "components/Buttons/Buttons";
import edit_icon from "../../../assets/img/edit_icon_filled.svg";
import upload from "../../../assets/img/upload_cover_letter_icon.svg"
import duplicate_icon from "../../../assets/img/duplicate_icon.svg";
import recycle_icon from "../../../assets/img/recycle_icon.svg";
import add_icon from "../../../assets/img/add_icon.svg";
import save_icon from "../../../assets/img/save_icon2.svg";
import drag_icon from "../../../assets/img/drag_icon.svg";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import BasicInfoSection from "./BasicInfoSection";
import opened_eye_icon from '../../../assets/img/open_eye_icon.svg';
import closed_eye_icon from '../../../assets/img/closed_eye_icon.svg';
import cancel_icon from "../../../assets/img/cancel_icon.svg";
import { NewSearchButton } from "components/Buttons/Buttons";

// API Configuration
const apiUrl = process.env.REACT_APP_API_URL || "";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    fontFamily: "Poppins",
    borderColor: "transparent",
    backgroundColor: "#f8fafc",
    transition: "all 0.3s ease",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#e2e8f0",
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#cbd5e1",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.2)",
    },
    "& .MuiOutlinedInput-input": {
      minHeight: "20px",
      padding: "12px 16px",
      color: "#1e293b",
    },
  },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: "#64748b",
  fontSize: "14px",
  fontFamily: "Poppins",
  fontWeight: 500,
  mb: 1,
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  color: "#1e293b",
  fontSize: "20px",
  fontFamily: "Poppins",
  fontWeight: 600,
  marginBottom: "16px",
}));
const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: "none",
  borderRadius: "16px",
  border: "1px solid #e2e8f0",
  marginBottom: "24px",
  padding: "20px",
  backgroundColor: "#ffffff",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.05)",
    borderColor: "#cbd5e1",
  },
}));

const StyledPaper = styled(Box)(({ theme }) => ({
  boxShadow: "none",
  borderRadius: "16px",
  border: "1px solid #e2e8f0",
  padding: "24px",
  marginBottom: "16px",
  marginTop: "16px",
  backgroundColor: "#ffffff",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.05)",
    borderColor: "#cbd5e1",
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "20px",
  paddingBottom: "12px",
  borderBottom: "1px solid #e2e8f0",
}));

const CancelButton = styled(Button)(({ theme }) => ({
  width: 115,
  height: 48,
  borderRadius: '10px',
  borderWidth: '1px',
  gap: '10px',
  paddingTop: '14px',
  paddingRight: '24px',
  paddingBottom: '14px',
  paddingLeft: '24px',
  border: '1px solid #FF0000',
  color: '#FF0000',
  "&:hover": {
    backgroundColor: "#FF0000",
    color: "#fff",
  },
}));

const ResumeEditor = ({ percentageContainer, buttonsContainer }) => {
  const theme = useTheme();

  // Core state
  const [isEditing, setIsEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [currentSection, setCurrentSection] = useState("");
  const [newItemValue, setNewItemValue] = useState("");
  const [newSectionName, setNewSectionName] = useState("");
  const [customSections, setCustomSections] = useState({});
  const [sectionTemplates, setSectionTemplates] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [sectionConfig, setSectionConfig] = useState({});
  const [initialData, setInitialData] = useState({});
  const [sectionOrder, setSectionOrder] = useState([]);

  // Enhanced state management
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [helpMode, setHelpMode] = useState(false);
  const autoSaveTimer = useRef(null);

  const user = useFetchUser();
  const userId = useUserId();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resumeData, setResumeData] = useState(initialData);
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [freeTrial, setFreeTrial] = useState();
  const [freeTrialLoading, setFreeTrialLoading] = useState(false);
  const [canModifyResume, setCanModifyResume] = useState(true);
  const [freeTrialMessage, setFreeTrialMessage] = useState("");
  const [showFreeTrialDialog, setShowFreeTrialDialog] = useState(false);
  const [realTimeCountdown, setRealTimeCountdown] = useState(null);
  const countdownInterval = useRef(null);
  const [remainingTimeFormatted, setRemainingTimeFormatted] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  // Backend profile score state
  const [backendProfileScore, setBackendProfileScore] = useState(null);
  const [profileBreakdown, setProfileBreakdown] = useState(null);

  // Password change state
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Email change tracking
  const [originalEmail, setOriginalEmail] = useState("");

  const [emailValidity, setEmailValidity] = useState({ available: true, error: "" }); // Track email validity
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordValues, setPasswordValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [authMethod, setAuthMethod] = useState(null);
  const [has_password, setHasPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePreview = () => {
    previewPDFInNewTab();
    handleClose();
  };

  const handleDownload = () => {
    downloadPDF();

    handleClose();
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}candidates/api/resume_preview/${userId.userId}/`,
        {
          responseType: "blob",
        }
      );

      // Create a blob URL
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = `resume.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (response.status === 404) {
        showSnackbar("Resume not found", "error");
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  // Method 2: Open PDF in new tab/window for preview
  const previewPDFInNewTab = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}candidates/api/resume_preview/${userId.userId}/`,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Open in new tab
      window.open(url, "_blank");
      if (response.status === 404) {
        showSnackbar("Resume not found", "error");
        // showSnackbar(
        //     t("snackbars.Resume_not_found", {
        //       time: response.data.remaining_time_formatted
        //     }),
        //     { severity: "error", duration: 2000 }
        //   );
      }
    } catch (error) {
      console.error("Error previewing PDF:", error);
    }
  };

  useEffect(() => {
    if (resumeData && Object.keys(resumeData).length > 0) {
      const coreFields = ["Name", "Email", "Phone", "LinkedIn", "Profil", "Address", "DateNaissance"];
      const metadataFields = [
        "agexperiece",
        "Section Metadata",
        "sectionMetadata",
        "sectionTemplates",
        "customSections",
        "sectionConfig",
        "userId",
        "Age",
        "profile_score",
      ];

      const sections = Object.keys(resumeData).filter(
        (key) => !coreFields.includes(key) && !metadataFields.includes(key)
      );

      // Only update if sectionOrder is empty or different
      if (
        sectionOrder.length === 0 ||
        JSON.stringify(sections.sort()) !== JSON.stringify(sectionOrder.sort())
      ) {
        setSectionOrder(sections);
      }
    }
  }, [resumeData]);
  
  // Initialize original email when data is loaded
  useEffect(() => {
    if (resumeData?.Email && !originalEmail) {
      setOriginalEmail(resumeData.Email);
    }
  }, [resumeData?.Email]);

  // Authentication check
  useEffect(() => {
    if (localStorage.getItem("accessToken")) {
      // console.log("user is :: ", user.formData.id);
    } else {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (userId && userId.userId) {
      fetchFreeTrial(userId.userId);
      fetchInitialData();
      fetchProfileScore(userId.userId);
      checkAuthMethod();
    }
  }, [userId]);
  
  // Fetch profile score from backend
  const fetchProfileScore = async (candidateId) => {
    try {
      const response = await axios.get(
        `${apiUrl}candidates/api/profile_score/${candidateId}/`
      );
      
      if (response.data.success) {
        setBackendProfileScore(response.data.total_score);
        setProfileBreakdown(response.data.breakdown);
        console.log('Profile score loaded from backend:', response.data.total_score);
      }
    } catch (error) {
      console.error('Error fetching profile score:', error);
      // Fallback to frontend calculation if backend fails
      setBackendProfileScore(null);
    }
  };

  // Password management functions
  const checkAuthMethod = async () => {
    try {
      const response = await fetch(
        `${apiUrl}authentication/check-auth-method/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          }
        }
      );
      const data = await response.json();
      setAuthMethod(data.auth_method);
      setHasPassword(data.has_password);
    } catch (error) {
      console.error("Error checking auth method:", error);
    }
  };

  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);

    // Check if new password and confirm password match
    if (passwordValues.newPassword !== passwordValues.confirmPassword) {
      showSnackbar(t("personalInformation.passwords_do_not_match"), { severity: "error" });
      setPasswordLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${apiUrl}authentication/change-password/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: JSON.stringify({
            user: userId.userId,
            old_password: passwordValues.currentPassword,
            new_password: passwordValues.newPassword,
          }),
        }
      );

      if (response.ok) {
        showSnackbar(t("personalInformation.password_updated"), { severity: "success" });
        setIsEditingPassword(false);
        setPasswordValues({
          currentPassword: "",
          newPassword: "",
          confirmPassword: ""
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || t("personalInformation.password_update_failed"));
      }
    } catch (error) {
      console.error("Error updating password:", error);
      showSnackbar(error.message || t("personalInformation.password_update_failed"), { severity: "error" });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCancelPassword = () => {
    setIsEditingPassword(false);
    // Reset password fields when cancelling
    setPasswordValues({
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
  };

  // Auto-save functionality
  useEffect(() => {
    if (isEditing && isDirty && resumeData) {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }

      autoSaveTimer.current = setTimeout(() => {
        handleAutoSave();
      }, 5000); // Auto-save after 5 seconds of inactivity
    }

    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [resumeData, isEditing, isDirty]);

  // Scroll to top functionality
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollToTop(window.pageYOffset > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    console.log("freeTrial is : ", freeTrial);
  }, [freeTrial]);
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyboard = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case "z":
            if (e.shiftKey) {
              handleRedo();
            } else {
              handleUndo();
            }
            e.preventDefault();
            break;
          case "s":
            if (isEditing) {
              saveResumeAsJson();
              e.preventDefault();
            }
            break;
          case "e":
            if (!isEditing) {
              startEditing();
              e.preventDefault();
            }
            break;
        }
      }

      // ESC to close dialogs
      if (e.key === "Escape" && openDialog) {
        handleDialogClose();
      }
    };

    window.addEventListener("keydown", handleKeyboard);
    return () => window.removeEventListener("keydown", handleKeyboard);
  }, [isEditing, openDialog, undoStack, redoStack]);

  const fetchFreeTrial = async (userId) => {
    console.log("🔍 fetchFreeTrial called with userId:", userId);
    if (!userId) return;

    setFreeTrialLoading(true);
    try {
      const url = `${apiUrl}candidates/api/get-free-trial-counter/?user_id=${userId}`;
      console.log("🔗 API URL:", url);

      const response = await axios.get(url);

      console.log("📝 fetchFreeTrial response:", response.data);

      // if (response.data.success) {
      setFreeTrial(response.data.count);
      setCanModifyResume(response.data.can_modify);
      setFreeTrialMessage(response.data.message);
      setRemainingTimeFormatted(response.data.cooldown);
      console.log("✅ State updated:", {
        canModifyResume: response.data.can_modify,
        count: response.data.count,
        inCooldown: response.data.in_cooldown,
      });

      // Start countdown if user is in cooldown OR has reached the limit
      if (
        (response.data.in_cooldown || response.data.count >= 3) &&
        response.data.remaining_time_seconds > 0 &&
        response.data.last_modification
      ) {
        console.log("🕐 Starting countdown with:", {
          lastModification: response.data.last_modification,
          remainingSeconds: response.data.remaining_time_seconds,
          inCooldown: response.data.in_cooldown,
          count: response.data.count,
        });
        startCountdown(
          response.data.last_modification,
          response.data.remaining_time_seconds
        );
      } else if (response.data.count >= 3 && !response.data.can_modify) {
        // If count >= 3 but no remaining time, show static cooldown message
        console.log(
          "⏰ User at limit but no active countdown, showing static time"
        );
        setRealTimeCountdown(
          response.data.remaining_time_formatted || "0h 0m 0s"
        );
      } else {
        stopCountdown();
      }

      // Show warning if user has limited modifications remaining
      if (
        response.data.remaining_modifications <= 1 &&
        response.data.remaining_modifications > 0
      ) {
        showSnackbar(
          t("snackbars.resume_modified_remaining", {
            count: response.data.remaining_modifications,
            plural: response.data.remaining_modifications === 1 ? "" : "s",
          }),
          { severity: "warning", duration: 6000 }
        );
      }

      // Show cooldown warning if in cooldown period
      /* if (response.data.in_cooldown) {
        showSnackbar(
          t("snackbars.free_trial_limit_reached", {
            time: response.data.remaining_time_formatted,
          }),
          { severity: "info", duration: 2000 }
        );
      } */
       setCanModifyResume(true); // Always allow modification
      // } else {
      //   console.error('❌ Failed to fetch free trial data:', response.data.error);
      //   setCanModifyResume(true); // Allow modifications if API fails
      // }
    } catch (error) {
      console.error("❌ Error fetching free trial data:", error);
      setCanModifyResume(true); // Allow modifications if API fails
    } finally {
      setFreeTrialLoading(false);
    }
  };

  // Free trial management functions
  const incrementFreeTrialCounter = async () => {
    console.log("📈 incrementFreeTrialCounter called");

    if (!userId?.userId) {
      console.log("❌ No userId available");
      return { success: false, error: t("ui_text.user_id_not_available") };
    }

    console.log("💬 API call with userId:", userId.userId);

    try {
      const response = await axios.post(
        `${apiUrl}candidates/api/increment-free-trial-counter/`,
        { user_id: userId.userId }
      );

      console.log("📝 API Response:", response.data);

      if (response.data.success) {
        // Update local state
        setFreeTrial(response.data.count);
        setCanModifyResume(response.data.can_modify);
        setFreeTrialMessage(response.data.message);

        // Show appropriate message
        if (response.data.remaining_modifications > 0) {
          showSnackbar(
            t("snackbars.resume_modified_remaining", {
              count: response.data.remaining_modifications,
              plural: response.data.remaining_modifications === 1 ? "" : "s",
            }),
            { severity: "success", duration: 4000 }
          );
        } else if (response.data.count >= 3) {
          showSnackbar(t("snackbars.final_free_modification"), {
            severity: "warning",
            duration: 8000,
          });

          // Immediately refresh trial status to get cooldown info
          setTimeout(() => {
            if (userId?.userId) {
              fetchFreeTrial(userId.userId);
            }
          }, 500); // Small delay to ensure backend has processed
        }

        return { success: true, data: response.data };
      } else {
        console.log("❌ API returned error:", response.data);
        return {
          success: false,
          error: response.data.message || response.data.error,
        };
      }
    } catch (error) {
      console.error("❌ API call failed:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      return { success: false, error: t("ui_text.failed_to_update_counter") };
    }
  };

  const checkFreeTrialBeforeModification = async () => {
    // console.log("🔄 checkFreeTrialBeforeModification called - BYPASSED");
    return true;
  };

  // Real-time countdown functions
  const calculateRemainingTime = useCallback(
    (lastModificationTime, totalSeconds) => {
      const now = new Date();
      const lastModification = new Date(lastModificationTime);
      const timePassed = Math.floor((now - lastModification) / 1000);
      const remaining = Math.max(0, 3 * 60 * 60 - timePassed); // 3 hours in seconds

      const hours = Math.floor(remaining / 3600);
      const minutes = Math.floor((remaining % 3600) / 60);
      const seconds = remaining % 60;

      return {
        totalSeconds: remaining,
        formatted: `${hours}h ${minutes}m ${seconds}s`,
      };
    },
    []
  );

  const startCountdown = useCallback(
    (lastModificationTime, initialSeconds) => {
      console.log("⏰ Starting countdown timer");

      // Clear any existing interval
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }

      const updateCountdown = () => {
        const remaining = calculateRemainingTime(
          lastModificationTime,
          initialSeconds
        );

        if (remaining.totalSeconds <= 0) {
          console.log("⏰ Countdown finished - refreshing trial status");
          setRealTimeCountdown("0h 0m 0s");
          stopCountdown();
          // Refresh the trial status when countdown ends
          if (userId?.userId) {
            fetchFreeTrial(userId.userId);
          }
        } else {
          setRealTimeCountdown(remaining.formatted);
        }
      };

      // Update immediately
      updateCountdown();

      // Set up interval for updates every second
      countdownInterval.current = setInterval(updateCountdown, 1000);
    },
    [calculateRemainingTime, userId]
  );

  const stopCountdown = useCallback(() => {
    console.log("⏰ Stopping countdown timer");
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }
    setRealTimeCountdown(null);
  }, []);

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, []);

  // // Real-time countdown functions
  // const calculateRemainingTime = useCallback((lastModificationTime, totalSeconds) => {
  //   if (!lastModificationTime || totalSeconds <= 0) return null;

  //   const lastModTime = new Date(lastModificationTime);
  //   const now = new Date();
  //   const timePassed = Math.floor((now - lastModTime) / 1000); // seconds passed
  //   const remaining = Math.max(0, (3 * 60 * 60) - timePassed); // 3 hours in seconds minus time passed

  //   if (remaining <= 0) return null;

  //   const hours = Math.floor(remaining / 3600);
  //   const minutes = Math.floor((remaining % 3600) / 60);
  //   const seconds = remaining % 60;

  //   return {
  //     totalSeconds: remaining,
  //     formatted: `${hours}h ${minutes}m ${seconds}s`,
  //     hours,
  //     minutes,
  //     seconds
  //   };
  // }, []);

  // const startCountdown = useCallback((lastModificationTime, initialSeconds) => {
  //   // Clear any existing interval
  //   if (countdownInterval.current) {
  //     clearInterval(countdownInterval.current);
  //   }

  //   // Calculate initial remaining time
  //   const initialRemaining = calculateRemainingTime(lastModificationTime, initialSeconds);
  //   setRealTimeCountdown(initialRemaining);

  //   if (!initialRemaining) return;

  //   // Start the countdown interval
  //   countdownInterval.current = setInterval(() => {
  //     const remaining = calculateRemainingTime(lastModificationTime, initialSeconds);
  //     setRealTimeCountdown(remaining);

  //     // If countdown reaches zero, stop the interval and refresh trial status
  //     if (!remaining || remaining.totalSeconds <= 0) {
  //       clearInterval(countdownInterval.current);
  //       setRealTimeCountdown(null);
  //       // Refresh free trial status
  //       if (userId?.userId) {
  //         fetchFreeTrial(userId.userId);
  //       }
  //     }
  //   }, 1000); // Update every second
  // }, [calculateRemainingTime, userId, fetchFreeTrial]);

  // const stopCountdown = useCallback(() => {
  //   if (countdownInterval.current) {
  //     clearInterval(countdownInterval.current);
  //     countdownInterval.current = null;
  //   }
  //   setRealTimeCountdown(null);
  // }, []);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, []);
  const saveToUndoStack = useCallback(() => {
    setUndoStack((prev) => {
      const newStack = [...prev, JSON.parse(JSON.stringify(resumeData))];
      return newStack.slice(-20); // Keep only last 20 states
    });
    setRedoStack([]); // Clear redo stack when new action is performed
  }, [resumeData]);

  const handleUndo = useCallback(() => {
    if (undoStack.length > 0) {
      const previousState = undoStack[undoStack.length - 1];
      setRedoStack((prev) => [...prev, JSON.parse(JSON.stringify(resumeData))]);
      setResumeData(previousState);
      setUndoStack((prev) => prev.slice(0, -1));
      setIsDirty(true);
    }
  }, [undoStack, resumeData]);

  const handleRedo = useCallback(() => {
    if (redoStack.length > 0) {
      const nextState = redoStack[redoStack.length - 1];
      setUndoStack((prev) => [...prev, JSON.parse(JSON.stringify(resumeData))]);
      setResumeData(nextState);
      setRedoStack((prev) => prev.slice(0, -1));
      setIsDirty(true);
    }
  }, [redoStack, resumeData]);

  const validateResumeData = useCallback((data) => {
    const errors = {};

    // Validate required fields
    if (!data.Name?.trim()) {
      errors.Name = t("validation_errors.full_name_required");
    }

    if (!data.Email?.trim()) {
      errors.Email = t("validation_errors.email_required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.Email)) {
      errors.Email = t("validation_errors.valid_email_required");
    }

    if (!data.Phone?.trim()) {
      errors.Phone = t("validation_errors.phone_required");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, []);

  const resumeCompletionPercentage = useMemo(() => {
    // Always calculate locally for instant feedback
    // The backend score is used for persistence but frontend should reflect current state
    if (!resumeData) return 0;

    const fieldsToCheck = [
      "Name", "Email", "Phone", "LinkedIn", "Profil", "Address", "DateNaissance",
      "Skills", "Language", "Certificates", "Education", "EXPERIENCE"
    ];
    
    const weightPerField = 100 / fieldsToCheck.length;
    let score = 0;

    fieldsToCheck.forEach((field) => {
      const value = resumeData[field];
      let isCompleted = false;
      
      if (typeof value === 'string') {
        isCompleted = value.trim().length > 0;
      } else if (Array.isArray(value)) {
        // Check if array has items AND valid content (not just empty strings)
        isCompleted = value.length > 0 && value.some(item => {
          if (typeof item === 'string') return item.trim().length > 0;
          return true; // For objects/other types, assume existence is enough for now
        });
      } else if (value !== null && value !== undefined) {
        isCompleted = true;
      }
      
      if (isCompleted) {
        score += weightPerField;
      }
    });

    return Math.round(score);
  }, [resumeData, backendProfileScore]);

  const getProgressColor = (progress) => {
    if (progress <= 25) return "#F44336"; // Error red
    if (progress <= 50) return "#FF9800"; // Orange
    if (progress <= 75) return "#FFD700"; // Gold
    return "#00C853"; // Success green
  };

  const renderProgressIndicator = (
    size = 60,
    strokeWidth = 4,
    fontSize = "14px",
    showLabel = false
  ) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <div
        style={{
          position: "relative",
          width: `${size}px`,
          height: `${size}px`,
        }}
      >
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={(size - strokeWidth) / 2}
            fill="none"
            stroke="rgba(0,0,0,0.1)"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={(size - strokeWidth) / 2}
            fill="none"
            stroke={getProgressColor(resumeCompletionPercentage)}
            strokeWidth={strokeWidth}
            strokeDasharray={`${2 * Math.PI * ((size - strokeWidth) / 2)}`}
            strokeDashoffset={`${
              2 *
              Math.PI *
              ((size - strokeWidth) / 2) *
              (1 - resumeCompletionPercentage / 100)
            }`}
            strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 0.5s ease" }}
          />
        </svg>
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: fontSize,
              fontWeight: 700,
              color: "#1e293b",
            }}
          >
            {resumeCompletionPercentage}%
          </div>
        </div>
      </div>
      {showLabel && (
        <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
          {t("resume_editor.Profile Completeness")}
        </Typography>
      )}
    </Box>
  );

  const handleAutoSave = useCallback(async () => {
    if (!validateResumeData(resumeData)) return;

    setIsAutoSaving(true);
    try {
      // Auto-save to localStorage as backup
      localStorage.setItem(
        "resumeBackup",
        JSON.stringify({
          data: resumeData,
          timestamp: new Date().toISOString(),
          sectionTemplates,
          sectionOrder,
        })
      );

      showSnackbar(t("snackbars.auto_saved_successfully"), {
        severity: "info",
        duration: 2000,
      });
      setIsDirty(false);
    } catch (error) {
      console.error("Auto-save failed:", error);
    } finally {
      setIsAutoSaving(false);
    }
  }, [
    resumeData,
    sectionTemplates,
    sectionOrder,
    validateResumeData,
    showSnackbar,
  ]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const loadBackup = useCallback(() => {
    try {
      const backup = localStorage.getItem("resumeBackup");
      if (backup) {
        const {
          data,
          sectionTemplates: savedTemplates,
          sectionOrder: savedOrder,
        } = JSON.parse(backup);
        setResumeData(data);
        setSectionTemplates(savedTemplates);
        setSectionOrder(savedOrder);
        showSnackbar(t("snackbars.backup_loaded_successfully"), {
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Failed to load backup:", error);
      showSnackbar(t("snackbars.failed_to_load_backup"), { severity: "error" });
    }
  }, [showSnackbar]);

  const transformResumeData = (apiData) => {
    if (!apiData) return null;

    const transformed = {};

    // Core fields that should not be treated as arrays
    const coreFields = ["Name", "Email", "Phone", "LinkedIn", "Profil", "Age", "DateNaissance"];

    const metadataFields = [
      "agexperiece",
      "Section Metadata",
      "sectionMetadata",
      "sectionTemplates",
      "customSections",
      "sectionConfig",
      "type",
      "icon",
      "displayName",
      "fields",
      "userId",
      "Age",
      "profile_score",
    ];

    Object.keys(apiData).forEach((key) => {
      const value = apiData[key];

      // Skip metadata fields
      if (metadataFields.includes(key)) {
        return;
      }

      // Handle core fields directly
      if (coreFields.includes(key)) {
        transformed[key] = value;
        return;
      }

      // Handle Education specifically
      if (key === "Education") {
        if (Array.isArray(value)) {
          transformed.Education = value.map((edu) => {
            if (typeof edu === "object" && edu !== null) {
              // Filter out configuration fields from individual items
              const cleanEdu = filterConfigFields(edu);

              if (cleanEdu.text && cleanEdu.date_range) {
                return {
                  title: cleanEdu.text || "",
                  subtitle: "",
                  date_range: cleanEdu.date_range || "",
                  description: "",
                };
              }
              return {
                title: cleanEdu.title || cleanEdu.institution || "",
                subtitle: cleanEdu.subtitle || cleanEdu.degree || "",
                date_range: cleanEdu.date_range || "",
                description: cleanEdu.description || "",
              };
            }
            return edu;
          });
        } else if (typeof value === "object" && value !== null) {
          const cleanEdu = filterConfigFields(value);
          transformed.Education = [
            {
              title:
                cleanEdu.title || cleanEdu.institution || cleanEdu.text || "",
              subtitle: cleanEdu.subtitle || cleanEdu.degree || "",
              date_range: cleanEdu.date_range || "",
              description: cleanEdu.description || "",
            },
          ];
        } else {
          transformed.Education = [];
        }
        return;
      }

      if (key === "EXPERIENCE") {
        if (Array.isArray(value)) {
          transformed.EXPERIENCE = value.map((exp) => {
            if (typeof exp === "object" && exp !== null) {
              // Filter out configuration fields from individual items
              const cleanExp = filterConfigFields(exp);

              if (cleanExp.company || cleanExp.position) {
                let description = "";
                if (
                  cleanExp.responsibilities &&
                  Array.isArray(cleanExp.responsibilities)
                ) {
                  description = cleanExp.responsibilities.join("\n• ");
                  if (description) description = "• " + description;
                }

                return {
                  title: cleanExp.position || cleanExp.title || "",
                  subtitle: cleanExp.company || cleanExp.subtitle || "",
                  date_range: cleanExp.date_range || "",
                  description: description || cleanExp.description || "",
                };
              } else if (cleanExp.text && cleanExp.date_range) {
                return {
                  title: cleanExp.text || "",
                  subtitle: "",
                  date_range: cleanExp.date_range || "",
                  description: "",
                };
              }
              return {
                title: cleanExp.title || "",
                subtitle: cleanExp.subtitle || "",
                date_range: cleanExp.date_range || "",
                description: cleanExp.description || "",
              };
            }
            return exp;
          });
        } else if (typeof value === "object" && value !== null) {
          const cleanExp = filterConfigFields(value);
          let description = "";
          if (
            cleanExp.responsibilities &&
            Array.isArray(cleanExp.responsibilities)
          ) {
            description = cleanExp.responsibilities.join("\n• ");
            if (description) description = "• " + description;
          }

          transformed.EXPERIENCE = [
            {
              title: cleanExp.position || cleanExp.title || cleanExp.text || "",
              subtitle: cleanExp.company || cleanExp.subtitle || "",
              date_range: cleanExp.date_range || "",
              description: description || cleanExp.description || "",
            },
          ];
        } else {
          transformed.EXPERIENCE = [];
        }
        return;
      }

      // Handle all other fields dynamically
      if (Array.isArray(value)) {
        // If it's an array of objects, check if they need transformation
        if (
          value.length > 0 &&
          typeof value[0] === "object" &&
          value[0] !== null
        ) {
          // Check if this looks like experience/education data
          const firstItem = value[0];
          if (
            firstItem.company ||
            firstItem.position ||
            firstItem.institution ||
            firstItem.degree ||
            firstItem.text
          ) {
            // Transform to standard object format
            transformed[key] = value.map((item) => ({
              title:
                item.title || item.position || item.text || item.name || "",
              subtitle: item.subtitle || item.company || item.institution || "",
              date_range: item.date_range || item.duration || "",
              description:
                item.description ||
                (item.responsibilities
                  ? item.responsibilities.join("\n• ")
                  : "") ||
                "",
            }));
          } else {
            // Keep as is for other object arrays
            transformed[key] = value;
          }
        } else {
          // Simple array (strings, numbers, etc.)
          transformed[key] = value;
        }
      } else if (typeof value === "object" && value !== null) {
        // Single object - convert to array for consistency
        transformed[key] = [value];
      } else if (typeof value === "string") {
        // **FIX: Handle string values properly**
        // If it's a string that looks like it should be an array (comma-separated), split it
        if (value.includes(",") && !coreFields.includes(key)) {
          transformed[key] = value
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item);
        } else {
          // For simple sections, wrap single strings in an array
          // But for text sections, keep as string
          const isTextSection =
            key.toLowerCase().includes("summary") ||
            key.toLowerCase().includes("objective") ||
            key.toLowerCase().includes("about") ||
            key.toLowerCase().includes("description");

          if (isTextSection) {
            transformed[key] = value;
          } else {
            transformed[key] = [value];
          }
        }
      } else {
        // **FIX: Ensure non-core fields are arrays by default**
        if (!coreFields.includes(key)) {
          transformed[key] = [];
        } else {
          transformed[key] = value;
        }
      }
    });

    // Handle Age to agexperiece mapping
    if (transformed.Age && !transformed.agexperiece) {
      transformed.agexperiece = transformed.Age;
    }

    // Ensure core fields have default values
    transformed.Name = transformed.Name || "";
    transformed.Email = transformed.Email || "";
    transformed.Phone = transformed.Phone || "";
    transformed.LinkedIn = transformed.LinkedIn || "";
    transformed.Profil = transformed.Profil || "";

    return transformed;
  };

  // Enhanced fetchInitialData function
  const fetchInitialData = async () => {
    // Guard clause: Ensure userId is available before proceeding
    if (!userId || !userId.userId) {
      console.log("fetchInitialData: userId not available yet, skipping...");
      return;
    }

    console.log("userId.userId", userId.userId);
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}candidates/fetch_resume_details/`,
        {
          params: {
            candidate_id: userId.userId,
          },
        }
      );

      if (response.data.success && response.data.resume_details) {
        // Transform the data to handle dynamic fields
        const transformedData = transformResumeData(
          response.data.resume_details
        );
        console.log("response.data.resume_details", response.data);
        // Set both initialData and resumeData
        setInitialData(transformedData);
        setResumeData(transformedData);

        console.log("Original API data:", response.data.resume_details);
        console.log("Transformed data:", transformedData);

        // Initialize section templates for dynamic sections
        initializeSectionTemplates(transformedData);
      } else {
        console.error("API response format error:", response.data);
        setError(t("ui_text.invalid_response_format"));
      }
    } catch (err) {
      console.error("Error fetching resume details:", err);
      setError(t("ui_text.failed_to_load_resume"));

      // Set empty default data if fetch fails
      const defaultData = {
        Name: "",
        Email: "",
        Phone: "",
        LinkedIn: "",
        Profil: "",
        Skills: [],
        Language: [],
        Certificates: [],
        Address: [],
        Education: [],
        EXPERIENCE: [],
        // agexperiece: 0,
      };
      setInitialData(defaultData);
      setResumeData(defaultData);
      initializeSectionTemplates(defaultData);
    } finally {
      setLoading(false);
    }
  };

  const filterConfigFields = (obj) => {
    if (typeof obj !== "object" || obj === null) {
      return obj;
    }

    const configFields = ["type", "icon", "displayName", "fields", "userId"];
    const filtered = {};

    Object.keys(obj).forEach((key) => {
      if (!configFields.includes(key)) {
        filtered[key] = obj[key];
      }
    });

    return filtered;
  };

  const initializeSectionTemplates = (data) => {
    const templates = {};
    const config = {};

    const knownSections = {
      Skills: {
        type: "simple",
        icon: "🛠️",
        displayName: t("resume_editor.Skills"),
      },
      Language: {
        type: "simple",
        icon: "🌐",
        displayName: t("resume_editor.Languages"),
      },
      Certificates: {
        type: "simple",
        icon: "📜",
        displayName: t("resume_editor.Certificates"),
      },
      Address: {
        type: "simple",
        icon: "📍",
        displayName: t("resume_editor.Address"),
      },
      Education: {
        type: "object",
        icon: "🎓",
        displayName: t("resume_editor.Education"),
      },
      EXPERIENCE: {
        type: "object",
        icon: "💼",
        displayName: t("resume_editor.Experience"),
      },
    };

    // Core fields that should not be treated as sections
    const coreFields = [
      t("resume_editor.Name"),
      t("resume_editor.Email"),
      t("resume_editor.Phone"),
      t("resume_editor.LinkedIn"),
      t("resume_editor.Profile"),
      t("resume_editor.Age"),
      "DateNaissance",
    ];

    // Metadata fields to exclude from section creation
    const metadataFields = [
      "agexperiece",
      "Section Metadata",
      "sectionMetadata",
      "sectionTemplates",
      "customSections",
      "sectionConfig",
      "userId",
      "Age",
      "profile_score",
    ];

    // Enhanced auto-detect section type based on data structure
    const detectSectionType = (value, sectionName) => {
      if (knownSections[sectionName]) {
        return knownSections[sectionName].type;
      }

      if (Array.isArray(value)) {
        if (value.length > 0) {
          const firstItem = value[0];
          if (typeof firstItem === "object" && firstItem !== null) {
            // Check if it looks like contact data (has label, value, type properties)
            if (
              firstItem.hasOwnProperty("label") &&
              firstItem.hasOwnProperty("value") &&
              firstItem.hasOwnProperty("type")
            ) {
              return "contact";
            }
            // Check if it looks like experience/education data
            if (
              firstItem.company ||
              firstItem.position ||
              firstItem.institution ||
              firstItem.degree ||
              firstItem.text ||
              firstItem.title
            ) {
              return "object";
            }
            return "object";
          }
        }
        return "simple";
      } else if (typeof value === "string") {
        return "text";
      }
      return "simple";
    };

    // Get appropriate icon for section
    const getSectionIcon = (sectionName) => {
      if (knownSections[sectionName]) {
        return knownSections[sectionName].icon;
      }

      // Auto-assign icons based on section name patterns
      const name = sectionName.toLowerCase();
      if (name.includes("skill")) return "🛠️";
      if (name.includes("language") || name.includes("lang")) return "🌐";
      if (name.includes("certificate") || name.includes("cert")) return "📜";
      if (name.includes("address") || name.includes("location")) return "📍";
      if (name.includes("education") || name.includes("school")) return "🎓";
      if (
        name.includes("experience") ||
        name.includes("work") ||
        name.includes("job")
      )
        return "💼";
      if (name.includes("project")) return "🚀";
      if (name.includes("award") || name.includes("achievement")) return "🏆";
      if (name.includes("hobby") || name.includes("interest")) return "🎨";
      if (name.includes("reference")) return "👥";
      if (name.includes("volunteer")) return "🤝";
      if (name.includes("publication")) return "📚";
      if (
        name.includes("contact") ||
        name.includes("phone") ||
        name.includes("email")
      )
        return "📞";
      return "📋"; // Default icon
    };

    // Create display name from section name
    const createDisplayName = (sectionName) => {
      if (knownSections[sectionName]) {
        return knownSections[sectionName].displayName;
      }

      // Convert camelCase or snake_case to proper title
      return sectionName
        .replace(/([A-Z])/g, " $1")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase())
        .trim();
    };

    // Initialize templates and config for ALL sections (except core fields and metadata)
    Object.keys(data).forEach((sectionName) => {
      // Skip core fields and metadata fields
      if (
        coreFields.includes(sectionName) ||
        metadataFields.includes(sectionName)
      ) {
        return;
      }

      const sectionType = detectSectionType(data[sectionName], sectionName);
      const sectionIcon = getSectionIcon(sectionName);
      const displayName = createDisplayName(sectionName);

      // Set fields based on section type
      let fields;
      switch (sectionType) {
        case "contact":
          fields = ["label", "value", "type"];
          break;
        case "object":
          fields = ["title", "subtitle", "date_range", "description"];
          break;
        case "text":
          fields = ["content"];
          break;
        case "simple":
        default:
          fields = ["item"];
          break;
      }

      templates[sectionName] = {
        type: sectionType,
        icon: sectionIcon,
        displayName: displayName,
        fields: fields,
        data: data[sectionName] || [],
      };

      config[sectionName] = templates[sectionName];
    });

    console.log("Generated section templates:", templates);
    setSectionTemplates(templates);
    setSectionConfig(config);
  };

  // Define section types and their configurations
  const sectionTypes = {
    simple: {
      name: t("resume_editor.simple.name"),
      description: t("resume_editor.simple.description"),
      fields: ["item"],
      icon: "📝",
    },
    object: {
      name: t("resume_editor.object.name"),
      description: t("resume_editor.object.description"),
      fields: ["title", "subtitle", "date_range", "description"],
      icon: "📋",
    },
    text: {
      name: t("resume_editor.text.name"),
      description: t("resume_editor.text.description"),
      fields: ["content"],
      icon: "📄",
    },
    contact: {
      name: t("resume_editor.contact.name"),
      description: t("resume_editor.contact.description"),
      fields: ["label", "value", "type"],
      icon: "📞",
    },
  };

  const renderDraggableSection = (sectionName, sectionData, index) => {
    const sectionType = getSectionType(sectionName);
    const isCore = isCoreSection(sectionName);
    const isBuiltIn = isBuiltInSection(sectionName);

    if (isCore) return null;

    return (
      <Draggable
        key={sectionName}
        draggableId={sectionName}
        index={index}
        isDragDisabled={!isEditing}
      >
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.draggableProps}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: "20px",
              border: "1px solid #E2E8F0",
              background: snapshot.isDragging
                ? "linear-gradient(180deg, #ffffff, #f7fafc)"
                : "linear-gradient(180deg, #ffffff, #fafafa)",
              transition:
                "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease",
              boxShadow: snapshot.isDragging
                ? "0 10px 30px rgba(0,0,0,0.10)"
                : "0 2px 6px rgba(0,0,0,0.04)",
              ...(isEditing
                ? {
                  "&:hover": {
                    boxShadow: "0 12px 32px rgba(0,0,0,0.10)",
                    borderColor: "#CBD5E1",
                    transform: "translateY(-2px)",
                  },
                }
                : {}),
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box display="flex" alignItems="center" gap={2}>
                {isEditing && (
                  <Box
                    {...provided.dragHandleProps}
                    sx={{
                      cursor: "grab",
                      display: "flex",
                      alignItems: "center",
                      color: "text.secondary",
                      fontSize: "12px",
                      "&:hover": { color: "primary.main" },
                      "& img": { width: 16, height: 16, opacity: 0.75 },
                    }}
                  >
                    {/* <DragIcon /> */}
                    <img src={drag_icon} />
                  </Box>
                )}
                <Avatar sx={{ bgcolor: getColorForSection(sectionName) }}>
                  {sectionTemplates[sectionName]?.icon || "📋"}
                </Avatar>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    color: "#0f172a",
                  }}
                >
                  {sectionTemplates[sectionName]?.displayName || sectionName}
                </Typography>
              </Box>

              {isEditing && sectionType !== "text" && (
                <Stack direction="row" spacing={1}>
                  <Tooltip
                    title={t("ui_text.add_item_tooltip")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      color="primary"
                      onClick={() => openAddDialog("add-item", sectionName)}
                    >
                      <img src={add_icon} alt={"add item"} />
                    </IconButton>
                  </Tooltip>

                  {!isBuiltIn && (
                    <Tooltip
                      title={t("ui_text.delete_section_tooltip")}
                      placement="top"
                      arrow
                    >
                      <IconButton
                        color="error"
                        onClick={() => deleteSection(sectionName)}
                      >
                        {/* <DeleteIcon /> */}
                        <img src={recycle_icon} alt={"delete"} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              )}
            </Box>

            {/* Section content with drag and drop */}
            {sectionType === "simple" &&
              renderDraggableSimpleSection(sectionName, sectionData)}
            {sectionType === "object" &&
              renderDraggableObjectSection(sectionName, sectionData)}
            {sectionType === "text" &&
              renderTextSection(sectionName, sectionData)}
            {sectionType === "contact" &&
              renderContactSection(sectionName, sectionData)}
          </Box>
        )}
      </Draggable>
    );
  };

  const createNewSection = (sectionName, sectionType, template = {}) => {
    const newSection = {
      type: sectionType,
      icon: template.icon || sectionTypes[sectionType].icon,
      displayName: template.displayName || sectionName,
      fields: template.fields || sectionTypes[sectionType].fields,
      data: [],
    };

    setSectionTemplates((prev) => ({
      ...prev,
      [sectionName]: newSection,
    }));

    setResumeData((prev) => ({
      ...prev,
      [sectionName]: [],
    }));

    setSectionConfig((prev) => ({
      ...prev,
      [sectionName]: newSection,
    }));
  };

  const deleteSection = (sectionName) => {
    const newResumeData = { ...resumeData };
    delete newResumeData[sectionName];
    setResumeData(newResumeData);

    const newTemplates = { ...sectionTemplates };
    delete newTemplates[sectionName];
    setSectionTemplates(newTemplates);

    const newConfig = { ...sectionConfig };
    delete newConfig[sectionName];
    setSectionConfig(newConfig);
  };

  const renderDraggableObjectSection = (sectionName, sectionData) => (
    <Droppable
      droppableId={sectionName}
      type="ITEM"
      isDropDisabled={!isEditing}
    >
      {(provided, snapshot) => (
        <List
          ref={provided.innerRef}
          {...provided.droppableProps}
          sx={{
            backgroundColor: snapshot.isDraggingOver
              ? "#e3f2fd"
              : "transparent",
            borderRadius: 1,
            transition: "background-color 0.2s ease",
          }}
        >
          {(sectionData || []).map((item, index) => (
            <Draggable
              key={`${sectionName}-${index}`}
              draggableId={`${sectionName}-${index}`}
              index={index}
              isDragDisabled={!isEditing}
            >
              {(provided, snapshot) => (
                <ListItem
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  divider
                  sx={{
                    boxShadow: snapshot.isDragging ? 2 : 0,
                    backgroundColor: snapshot.isDragging
                      ? "white"
                      : "transparent",
                    borderRadius: snapshot.isDragging ? 1 : 0,
                    mb: snapshot.isDragging ? 1 : 0,
                  }}
                >
                  {isEditing && (
                    <Box
                      {...provided.dragHandleProps}
                      sx={{
                        cursor: "grab",
                        mr: 2,
                        display: "flex",
                        alignItems: "center",
                        color: "text.secondary",
                        fontSize: "12px",
                        "&:hover": { color: "primary.main" },
                        "& img": { width: 12, height: 12 },
                      }}
                    >
                      {/* <DragIcon /> */}
                      <img src={drag_icon} />
                    </Box>
                  )}

                  <ListItemText
                    primary={
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily: "Poppins",
                          fontWeight: 600,
                          fontSize: "16px",
                        }}
                      >
                        {item.title ||
                          item.text ||
                          `${item.degree || ""} at ${item.institution || ""}`}
                      </Typography>
                    }
                    secondary={
                      <Stack spacing={0.5}>
                        {item.subtitle && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontFamily: "Poppins" }}
                          >
                            {item.subtitle}
                          </Typography>
                        )}
                        {item.date_range && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontFamily: "Poppins" }}
                          >
                            {item.date_range}
                          </Typography>
                        )}
                        {item.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontFamily: "Poppins" }}
                          >
                            {item.description}
                          </Typography>
                        )}
                      </Stack>
                    }
                  />

                  {isEditing && (
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title={t("ui_text.edit_tooltip")}>
                          <IconButton
                            color="primary"
                            onClick={() => openEditDialog(sectionName, index)}
                          >
                            {/* <EditIcon /> */}
                            <img src={edit_icon} alt={"edit"} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("ui_text.duplicate_tooltip")}>
                          <IconButton
                            color="primary"
                            onClick={() => duplicateItem(sectionName, index)}
                          >
                            <img
                              src={duplicate_icon}
                              style={{
                                filter:
                                  "brightness(0) saturate(100%) invert(23%) sepia(74%) saturate(1982%) hue-rotate(220deg) brightness(93%) contrast(98%)",
                                width: 19,
                                height: 19,
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("ui_text.delete_tooltip")}>
                          <IconButton
                            color="error"
                            onClick={() =>
                              removeItemFromSection(sectionName, index)
                            }
                          >
                            {/* <DeleteIcon /> */}
                            <img src={recycle_icon} alt={"delete"} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </List>
      )}
    </Droppable>
  );

  const renderDraggableSimpleSection = (sectionName, sectionData) => (
    <Droppable
      droppableId={sectionName}
      type="ITEM"
      isDropDisabled={!isEditing}
    >
      {(provided, snapshot) => (
        <Stack
          ref={provided.innerRef}
          {...provided.droppableProps}
          direction="row"
          spacing={1}
          flexWrap="wrap"
          gap={1}
          sx={{
            minHeight: "50px",
            p: 1,
            borderRadius: 1,
            backgroundColor: snapshot.isDraggingOver
              ? "#e3f2fd"
              : "transparent",
            transition: "background-color 0.2s ease",
          }}
        >
          {(sectionData || []).map((item, index) => (
            <Draggable
              key={`${sectionName}-${index}`}
              draggableId={`${sectionName}-${index}`}
              index={index}
              isDragDisabled={!isEditing}
            >
              {(provided, snapshot) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {isEditing && (
                      <Box
                        {...provided.dragHandleProps}
                        sx={{
                          cursor: "grab",
                          display: "flex",
                          alignItems: "center",
                          fontSize: "12px",
                          color: "text.secondary",
                          "& img": { width: 16, height: 16 },
                        }}
                      >
                        {/* <DragIcon fontSize="small" /> */}
                        <img src={drag_icon} />
                      </Box>
                    )}
                    <Chip
                      label={item}
                      sx={{
                        color: colors.primary,
                        backgroundColor: "rgba(59,130,246,0.06)",
                        border: `1px solid ${colors.primary}`,
                        borderColor: "rgba(59,130,246,0.35)",
                        boxShadow: snapshot.isDragging
                          ? "0 8px 18px rgba(0,0,0,0.08)"
                          : "none",
                        transform: snapshot.isDragging
                          ? "translateY(-1px)"
                          : "none",
                        "&:hover": {
                          backgroundColor: "rgba(59,130,246,0.09)",
                        },
                      }}
                      deleteIcon={
                        isEditing ? (
                          <img src={recycle_icon} alt={"delete"} />
                        ) : undefined
                      }
                      onDelete={
                        isEditing
                          ? () => removeItemFromSection(sectionName, index)
                          : undefined
                      }
                    />
                  </Box>
                </Box>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </Stack>
      )}
    </Droppable>
  );

  // Enhanced item management functions
  const addItemToSection = (sectionName, itemData) => {
    saveToUndoStack();
    const sectionType = getSectionType(sectionName);
    let processedData;

    switch (sectionType) {
      case "simple":
        processedData = typeof itemData === "string" ? itemData : itemData.item;
        break;
      case "object":
        processedData = {
          title: itemData.title || "",
          subtitle: itemData.subtitle || "",
          date_range: itemData.date_range || "",
          description: itemData.description || "",
        };
        break;
      case "text":
        processedData = itemData.content || itemData;
        break;
      case "contact":
        processedData = {
          label: itemData.label || "",
          value: itemData.value || "",
          type: itemData.type || "text",
        };
        break;
      default:
        processedData = itemData;
    }

    setResumeData((prev) => ({
      ...prev,
      [sectionName]: [...(prev[sectionName] || []), processedData],
    }));
    setIsDirty(true);
  };

  const updateItemInSection = (sectionName, itemIndex, newData) => {
    saveToUndoStack();
    setResumeData((prev) => ({
      ...prev,
      [sectionName]: prev[sectionName].map((item, index) =>
        index === itemIndex ? newData : item
      ),
    }));
    setIsDirty(true);
  };

  const removeItemFromSection = (sectionName, itemIndex) => {
    saveToUndoStack();
    setResumeData((prev) => ({
      ...prev,
      [sectionName]: prev[sectionName].filter(
        (_, index) => index !== itemIndex
      ),
    }));
    setIsDirty(true);
  };

  const duplicateItem = (sectionName, itemIndex) => {
    const item = resumeData[sectionName][itemIndex];
    const duplicatedItem = typeof item === "object" ? { ...item } : item;
    addItemToSection(sectionName, duplicatedItem);
  };

  // Helper functions
  const getSectionType = (sectionName) => {
    if (sectionTemplates[sectionName]) {
      return sectionTemplates[sectionName].type;
    }

    // Enhanced type detection for existing sections
    if (Array.isArray(resumeData[sectionName])) {
      if (resumeData[sectionName].length > 0) {
        const firstItem = resumeData[sectionName][0];
        if (typeof firstItem === "object" && firstItem !== null) {
          // Check if it looks like contact data
          if (
            firstItem.hasOwnProperty("label") &&
            firstItem.hasOwnProperty("value") &&
            firstItem.hasOwnProperty("type")
          ) {
            return "contact";
          }
          // Check if it looks like experience/education data
          if (
            firstItem.company ||
            firstItem.position ||
            firstItem.institution ||
            firstItem.degree ||
            firstItem.text ||
            firstItem.title
          ) {
            return "object";
          }
          return "object";
        }
      }
      return "simple";
    }
    return "text";
  };

  const getFieldsForSection = (sectionName) => {
    return (
      sectionTemplates[sectionName]?.fields ||
      sectionTypes[getSectionType(sectionName)].fields
    );
  };

  const isCoreSection = (sectionName) => {
    return ["Name", "Email", "Phone", "LinkedIn", "Profil"].includes(
      sectionName
    );
  };

  const isBuiltInSection = (sectionName) => {
    return [
      t("resume_editor.Skills"),
      t("resume_editor.Languages"),
      t("resume_editor.Certificates"),
      t("resume_editor.Education"),
      t("resume_editor.Experience"),
    ].includes(sectionName);
  };

  const openAddDialog = (type, sectionName = "", preSelectedType = "") => {
    setDialogType(type);
    setCurrentSection(sectionName);
    setOpenDialog(true);
    setEditingItem(null);

    if (type === "add-item") {
      const sectionType = getSectionType(sectionName);
      if (sectionType === "object") {
        setNewItemValue({
          title: "",
          subtitle: "",
          date_range: "",
          description: "",
        });
        setFormFields({
          title: "",
          subtitle: "",
          date_range: "",
          description: "",
        });
      } else if (sectionType === "contact") {
        setNewItemValue({
          label: "",
          value: "",
          type: "text",
        });
        setFormFields({
          label: "",
          value: "",
          type: "text",
        });
      } else {
        setNewItemValue("");
        setFormFields({});
      }
    } else if (type === "create-section" && preSelectedType) {
      // Set the pre-selected type for automatic dropdown selection
      setNewItemValue(preSelectedType);
      setFormFields({});
    } else {
      setNewItemValue("");
      setFormFields({});
    }

    setNewSectionName("");
  };

  const openEditDialog = (sectionName, itemIndex) => {
    const item = resumeData[sectionName][itemIndex];
    setDialogType("edit");
    setCurrentSection(sectionName);
    setEditingItem({ index: itemIndex, data: item });
    setOpenDialog(true);
  };

  const [formFields, setFormFields] = useState({});

  const handleDialogClose = () => {
    setOpenDialog(false);
    setNewItemValue("");
    setNewSectionName("");
    setEditingItem(null);
    setCurrentSection("");
    setFormFields({});
  };

  const apiUrl = process.env.REACT_APP_API_URL;

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [generatedCV, setGeneratedCV] = useState(null);
  const [editSnapshot, setEditSnapshot] = useState(null);

  const startEditing = () => {
    setEditSnapshot(JSON.parse(JSON.stringify(resumeData || {})));
    saveToUndoStack();
    setIsEditing(true);
    setIsDirty(false);
  };

  const cancelEditing = () => {
    if (editSnapshot) {
      setResumeData(editSnapshot);
      initializeSectionTemplates(editSnapshot);
    }
    setEditSnapshot(null);
    setIsEditing(false);
  };

  const showEnhancedToast = (filename, htmlContent) => {
    showSnackbar(
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          minWidth: "300px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: "500",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
          </svg>
          {t("resume_editor.CV Generated Successfully")}
        </div>

        <div
          style={{
            fontSize: "14px",
            opacity: 0.9,
            marginBottom: "8px",
          }}
        >
          {filename}
        </div>

        <div
          style={{
            display: "flex",
            gap: "8px",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={() => {
              const newTab = window.open("", "_blank");
              newTab.document.write(htmlContent);
              newTab.document.close();
            }}
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              color: "white",
              padding: "8px 16px",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              transition: "all 0.2s ease",
            }}
            onMouseOver={(e) => {
              e.target.style.background = "rgba(255, 255, 255, 0.3)";
            }}
            onMouseOut={(e) => {
              e.target.style.background = "rgba(255, 255, 255, 0.2)";
            }}
          >
            {t("resume_editor.Preview")}
          </button>
        </div>
      </div>,
      {
        severity: "success",
        position: "top-center",
        duration: 8000,
        style: {
          minWidth: "350px",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }
    );
  };

  const saveResumeAsJson = async (theme = "light") => {
    // Check free trial before allowing modification
    console.log("🔍 saveResumeAsJson: checking free trial status");

    // Wait for the latest trial status
    await fetchFreeTrial(userId.userId);

    console.log(
      "📊 saveResumeAsJson: current canModifyResume:",
      canModifyResume
    );

    if (!canModifyResume) {
      console.log("❌ saveResumeAsJson: Cannot modify, showing dialog");
      setShowFreeTrialDialog(true);
      return null;
    }

    console.log("✅ saveResumeAsJson: Can modify, proceeding with save");

    // Increment the counter
    const incrementResult = await incrementFreeTrialCounter();
    if (!incrementResult.success) {
      console.log("❌ saveResumeAsJson: Failed to increment counter");
      showSnackbar("Failed to track modification. Please try again.", {
        severity: "error",
      });
    }

    setLoading(true);
    try {
      const resumePayload = {
        userId: user.formData.id,
        Name: resumeData.Name || "",
        Profil: resumeData.Profil || "",
        Email: resumeData.Email || "",
        Phone: resumeData.Phone || "",
        LinkedIn: resumeData.LinkedIn || "",
        Skills: resumeData.Skills || [],
        Language: resumeData.Language || [],
        Certificates: resumeData.Certificates || [],
        Education: resumeData.Education || [],
        EXPERIENCE: resumeData.EXPERIENCE || [],
        profile_score: resumeCompletionPercentage, // Add profile score to payload
      };

      // Build complete payload...
      Object.entries(resumeData).forEach(([key, value]) => {
        if (!resumePayload.hasOwnProperty(key)) {
          resumePayload[key] = value || (Array.isArray(value) ? [] : "");
        }
      });

      Object.entries(sectionTemplates).forEach(([sectionName, template]) => {
        if (!resumePayload.hasOwnProperty(sectionName)) {
          switch (template.type) {
            case "simple":
              resumePayload[sectionName] = [];
              break;
            case "object":
              resumePayload[sectionName] = [];
              break;
            case "text":
              resumePayload[sectionName] = "";
              break;
            case "contact":
              resumePayload[sectionName] = [];
              break;
            default:
              resumePayload[sectionName] = [];
          }
        }
      });

      // DON'T persist sectionMetadata to backend
      // It's only used for UI state management in the frontend
      // Backend uses top-level fields as the single source of truth
      // This prevents data redundancy and synchronization issues

      const response = await axios.post(
        `${apiUrl}candidates/generate_resume_pdf/`,
        resumePayload
      );

      setLoading(false);

      if (response.data.success) {
        console.log("PDF generated:", response.data);

        // Check if email changed

        
        // Update profile score separately
        try {
          await axios.post(
            `${apiUrl}authentication/update_profile_score/`,
            {
              user_id: user.formData.id,
              profile_score: resumeCompletionPercentage,
            }
          );
          console.log("Profile score updated successfully");
          // Notify other components (Navbar) about the score update
          window.dispatchEvent(new CustomEvent('profileScoreUpdated', { 
            detail: { score: resumeCompletionPercentage }
          }));
        } catch (profileScoreError) {
          console.error("Error updating profile score:", profileScoreError);
          // Don't fail the entire operation if profile score update fails
        }

        showEnhancedToast(response.data.filename, response.data.html_content);
        
        // Show email change confirmation if email was changed


        return response.data;
      } else {
        showSnackbar(
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            <span>
              {t("resume_editor.Error")}: {response.data.message}
            </span>
          </div>,
          {
            severity: "error",
            position: "top-center",
            duration: 5000,
          }
        );
        return null;
      }
    } catch (error) {
      setLoading(false);
      console.error("PDF generation failed:", error);
      const errorMessage = error.response?.data?.message || error.message;
      showSnackbar(
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <span>
            {t("resume_editor.Failed to generate PDF")}: {errorMessage}
          </span>
        </div>,
        {
          severity: "error",
          position: "top-center",
          duration: 5000,
        }
      );
      return null;
    }
  };

  // Removed preview; using Cancel instead per request

  // Modal Component for CV Preview (Fixed)
  const CVPreviewModal = ({ open, onClose, cvData }) => {
    if (!open || !cvData) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          padding: "20px",
        }}
        onClick={(e) => {
          // Close modal when clicking outside
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            width: "95%",
            maxWidth: "1000px",
            height: "90vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "16px 24px",
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "#f8f9fa",
            }}
          >
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                {t("resume_editor.CV Preview")}
              </h2>
              <p
                style={{
                  margin: "4px 0 0 0",
                  fontSize: "14px",
                  color: "#666",
                }}
              >
                {cvData.filename}
              </p>
            </div>

            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <button
                onClick={() => {
                  // Create download link
                  const link = document.createElement("a");
                  link.href = `${apiUrl}download/${cvData.filename}`;
                  link.target = "_blank";
                  link.download = cvData.filename;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={{
                  background: "#1976d2",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "background-color 0.2s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "#1565c0";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "#1976d2";
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                </svg>
                {t("resume_editor.Download PDF")}
              </button>

              <button
                onClick={onClose}
                style={{
                  background: "transparent",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  width: "40px",
                  height: "40px",
                  cursor: "pointer",
                  color: "#666",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  transition: "all 0.2s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "#f5f5f5";
                  e.target.style.borderColor = "#999";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.borderColor = "#ddd";
                }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Content */}
          <div
            style={{
              flex: 1,
              overflow: "auto",
              padding: "20px",
              backgroundColor: "#f8f9fa",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
                minHeight: "100%",
              }}
            >
              <div
                dangerouslySetInnerHTML={{ __html: cvData.content }}
                style={{
                  padding: "20px",
                  lineHeight: "1.6",
                  color: "#333",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  <CVPreviewModal
    open={showPreviewModal}
    onClose={() => {
      setShowPreviewModal(false);
      setGeneratedCV(null);
    }}
    cvData={generatedCV}
  />;

  const handleDialogSubmit = () => {
    if (dialogType === "create-section") {
      if (!newSectionName.trim()) return;
      const sectionType = newItemValue || "simple";
      createNewSection(newSectionName.trim(), sectionType);
    } else if (dialogType === "edit" && editingItem) {
      updateItemInSection(currentSection, editingItem.index, editingItem.data);
    } else if (currentSection) {
      const sectionType = getSectionType(currentSection);
      let itemData;

      if (sectionType === "object") {
        // Use the newItemValue as an object instead of parsing pipe-separated string
        itemData = {
          title: newItemValue.title || "",
          subtitle: newItemValue.subtitle || "",
          date_range: newItemValue.date_range || "",
          description: newItemValue.description || "",
        };
      } else if (sectionType === "contact") {
        itemData = {
          label: newItemValue.label || "",
          value: newItemValue.value || "",
          type: newItemValue.type || "text",
        };
      } else {
        itemData = newItemValue.trim();
      }

      addItemToSection(currentSection, itemData);
    }

    handleDialogClose();
  };

  const renderLoadingOverlay = () => {
    if (!loading) return null;

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 3,
          position: "fixed", // or "absolute" depending on your layout
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(255, 255, 255, 0.8)", // Semi-transparent overlay
          backdropFilter: "blur(4px)", // Creates the blur effect
          WebkitBackdropFilter: "blur(4px)", // Safari support
          zIndex: 9999, // Ensures it's on top
        }}
      >
        <SmartHireLoader />
      </Box>
    );
  };
  // Helper functions for styling
  const getColorForSection = (sectionName) => {
    const colors = [
      "#1976d2",
      "#dc004e",
      "#4caf50",
      "#ff9800",
      "#9c27b0",
      "#00bcd4",
    ];
    let hash = 0;
    for (let i = 0; i < sectionName.length; i++) {
      hash = sectionName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getSectionIcon = (sectionName) => {
    const iconMap = {
      Skills: <StarIcon />,
      Language: <LanguageIcon />,
      Certificates: <CertificateIcon />,
      Education: <EducationIcon />,
      EXPERIENCE: <ExperienceIcon />,
      Projects: <ProjectIcon />,
      Hobbies: <HobbyIcon />,
      Awards: <AwardIcon />,
    };
    return iconMap[sectionName] || <SettingsIcon />;
  };

  const renderSectionManager = () => (
    <StyledPaper>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: "#9c27b0" }}>
            <SettingsIcon />
          </Avatar>
          <StyledTitle>Section Manager</StyledTitle>
        </Box>
        {isEditing && (
          <OutlinedButton
            // variant="contained"
            // startIcon={<AddIcon />}
            onClick={() => openAddDialog("create-section")}
          >
            <img src={add_icon} alt={"add item"} />
            <Typography
              sx={{
                ml: 2,
              }}
            >
              {t(`resume_editor.New Section`)}
            </Typography>
          </OutlinedButton>
        )}
      </Box>
      {isEditing && (
        <Grid container spacing={2}>
          {Object.entries(sectionTypes).map(([type, config]) => (
            <Grid item xs={12} sm={6} md={3} key={type}>
              <StyledCard>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h4" component="div" mb={1}>
                    {config.icon}
                  </Typography>
                  <StyledTitle variant="h6" component="div" mb={1}>
                    {config.name}
                  </StyledTitle>
                  <StyledTypography
                    variant="body2"
                    color="text.secondary"
                    mb={2}
                  >
                    {config.description}
                  </StyledTypography>
                </CardContent>
                <CardActions sx={{ justifyContent: "center" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // Set the type first, then open dialog
                      setNewItemValue(type);
                      // Pass the type instead of config.name to ensure proper selection
                      openAddDialog("create-section", "", type);
                    }}
                  >
                    {t(`resume_editor.Create`)} {config.name}
                  </Button>
                </CardActions>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      )}
    </StyledPaper>
  );

  const renderTextSection = (sectionName, sectionData) => (
    <Box mt={2}>
      {isEditing ? (
        <StyledTextField
          fullWidth
          multiline
          rows={6}
          value={sectionData || ""}
          onChange={(e) =>
            setResumeData((prev) => ({
              ...prev,
              [sectionName]: e.target.value,
            }))
          }
          placeholder={t("ui_text.enter_your_summary")}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              fontFamily: "Poppins",
              fontSize: "14px",
              padding: 2,
            },
          }}
        />
      ) : (
        <Typography
          variant="body1"
          sx={{
            fontFamily: "Poppins",
            fontSize: "14px",
            lineHeight: 1.7,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            padding: 2,
            borderRadius: "12px",
            backgroundColor: "#f9f9f9",
            border: "1px solid #e0e0e0",
            color: "#000000",
          }}
        >
          {sectionData || t("ui_text.no_content_added_yet")}
        </Typography>
      )}
    </Box>
  );
  const renderContactSection = (sectionName, sectionData) => (
    <Grid container spacing={2}>
      {(sectionData || []).map((item, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <StyledCard variant="outlined">
            <CardContent>
              <StyledTypography variant="subtitle2" color="text.secondary">
                {item.label}:
              </StyledTypography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  color: "#000000",
                  fontSize: "14px",
                }}
              >
                {item.value}
              </Typography>
            </CardContent>
            {isEditing && (
              <CardActions>
                <IconButton
                  color="primary"
                  onClick={() => openEditDialog(sectionName, index)}
                >
                  {/* <EditIcon /> */}
                  <img src={edit_icon} alt={"edit"} />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => removeItemFromSection(sectionName, index)}
                >
                  {/* <DeleteIcon /> */}
                  <img src={recycle_icon} />
                </IconButton>
              </CardActions>
            )}
          </StyledCard>
        </Grid>
      ))}
    </Grid>
  );
  const renderDialogContent = () => {
    if (dialogType === "create-section") {
      return (
        <Stack spacing={2} sx={{ mt: 0 }}>
          <StyledTypography>{t("resume_editor.Section Name")}</StyledTypography>
          <StyledTextField
            fullWidth
            // label="Section Name"
            value={newSectionName}
            onChange={(e) => setNewSectionName(e.target.value)}
            placeholder={t("resume_editor.Enter section name")}
            autoFocus
          />
          <FormControl fullWidth>
            <StyledTypography>
              {t("resume_editor.Section Type")}
            </StyledTypography>
            {/* <InputLabel>Section Type</InputLabel> */}
            <Select
              value={newItemValue}
              onChange={(e) => setNewItemValue(e.target.value)}
              // label="Section Type"
              sx={{ borderRadius: "10px" }}
            >
              <MenuItem value="">{t(`resume_editor.Select type...`)}</MenuItem>
              {Object.entries(sectionTypes).map(([type, config]) => (
                <MenuItem key={type} value={type}>
                  {config.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      );
    }

    if (dialogType === "edit" && editingItem) {
      return renderEditFields();
    }

    if (dialogType === "add-item") {
      const sectionType = getSectionType(currentSection);
      const fields = getFieldsForSection(currentSection);

      if (sectionType === "object") {
        return (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {fields.map((field) => (
              <>
                <StyledTypography>
                  {field.charAt(0).toUpperCase() +
                    field.slice(1).replace("_", " ")}
                </StyledTypography>
                <StyledTextField
                  key={field}
                  fullWidth
                  // label={
                  //   field.charAt(0).toUpperCase() +
                  //   field.slice(1).replace("_", " ")
                  // }
                  value={formFields[field] || ""}
                  onChange={(e) => {
                    setFormFields((prev) => ({
                      ...prev,
                      [field]: e.target.value,
                    }));
                    setNewItemValue((prev) => ({
                      ...prev,
                      [field]: e.target.value,
                    }));
                  }}
                  placeholder={t("ui_text.enter_field_placeholder", {
                    field: field.replace("_", " "),
                  })}
                  autoFocus={field === fields[0]}
                  multiline={field === "description"}
                  rows={field === "description" ? 3 : 1}
                />
              </>
            ))}
          </Stack>
        );
      }

      if (sectionType === "contact") {
        return (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <StyledTypography>{t("resume_editor.Label")}</StyledTypography>

            <StyledTextField
              fullWidth
              // label="Label"
              value={formFields.label || ""}
              onChange={(e) => {
                setFormFields((prev) => ({ ...prev, label: e.target.value }));
                setNewItemValue((prev) => ({ ...prev, label: e.target.value }));
              }}
              placeholder={t("ui_text.enter_label_placeholder")}
              autoFocus
            />
            <StyledTypography>{t("resume_editor.Value")}</StyledTypography>
            <StyledTextField
              fullWidth
              // label="Value"
              value={formFields.value || ""}
              onChange={(e) => {
                setFormFields((prev) => ({ ...prev, value: e.target.value }));
                setNewItemValue((prev) => ({ ...prev, value: e.target.value }));
              }}
              placeholder={t("ui_text.enter_value_placeholder")}
            />
            <FormControl fullWidth>
              <StyledTypography>{t("resume_editor.Type")}</StyledTypography>
              {/* <InputLabel>Type</InputLabel> */}
              <Select
                value={formFields.type || "text"}
                onChange={(e) => {
                  setFormFields((prev) => ({ ...prev, type: e.target.value }));
                  setNewItemValue((prev) => ({
                    ...prev,
                    type: e.target.value,
                  }));
                }}
                // label="Type"
                sx={{ borderRadius: "10px" }}
              >
                <MenuItem value="text">{t("ui_text.text_option")}</MenuItem>
                <MenuItem value="email">{t("ui_text.email_option")}</MenuItem>
                <MenuItem value="phone">{t("ui_text.phone_option")}</MenuItem>
                <MenuItem value="url">{t("ui_text.url_option")}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        );
      }

      // For simple sections
      return (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <StyledTypography>{t("resume_editor.Item Content")}</StyledTypography>
          <StyledTextField
            fullWidth
            // label="Item Content"
            value={newItemValue}
            onChange={(e) => setNewItemValue(e.target.value)}
            placeholder={t("resume_editor.Enter item")}
            autoFocus
          />
        </Stack>
      );
    }

    return null;
  };
  const renderEnhancedDialog = () => {
    if (!openDialog) return null;

    return (
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            border: "1.5px solid #D9D9D9",
          },
        }}
      >
        <DialogTitle>
          <StyledTitle>{getDialogTitle()}</StyledTitle>
        </DialogTitle>
        <DialogContent>{renderDialogContent()}</DialogContent>
        <DialogActions>
          <NewCancelButton onClick={handleDialogClose}>
            {t(`resume_editor.Cancel`)}
          </NewCancelButton>
          <OutlinedButton onClick={handleDialogSubmit}>
            {dialogType === t("resume_editor.edit")
              ? t("resume_editor.Update")
              : t("resume_editor.Add")}
          </OutlinedButton>
        </DialogActions>
      </Dialog>
    );
  };

  const renderEditFields = () => {
    const sectionType = getSectionType(currentSection);
    const fields = getFieldsForSection(currentSection);

    return (
      <Stack spacing={0} sx={{ mt: 1 }}>
        {fields.map((field) => (
          <>
            <StyledTypography>
              {field.charAt(0).toUpperCase() + field.slice(1).replace("_", " ")}
            </StyledTypography>
            <StyledTextField
              key={field}
              fullWidth
              sx={{ mb: 2 }}
              // label={
              //   field.charAt(0).toUpperCase() + field.slice(1).replace("_", " ")
              // }
              value={editingItem.data[field] || ""}
              onChange={(e) =>
                setEditingItem((prev) => ({
                  ...prev,
                  data: {
                    ...prev.data,
                    [field]: e.target.value,
                  },
                }))
              }
              placeholder={t("ui_text.enter_field_placeholder", {
                field: field.replace("_", " "),
              })}
            />
          </>
        ))}
      </Stack>
    );
  };

  const getDialogTitle = () => {
    switch (dialogType) {
      case "create-section":
        return t("resume_editor.Create New Section");
      case "edit":
        return t("ui_text.edit_item", { section: currentSection });
      case "add-item":
        return `${t("resume_editor.Add Item to")} ${currentSection}`;
      default:
        return t("ui_text.dialog_title");
    }
  };

  const handleDragEnd = (result) => {
    const { destination, source, type } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    if (type === "SECTION") {
      // Reorder sections
      const newSectionOrder = Array.from(sectionOrder);
      const [removed] = newSectionOrder.splice(source.index, 1);
      newSectionOrder.splice(destination.index, 0, removed);

      setSectionOrder(newSectionOrder);

      // Update section templates order
      const reorderedTemplates = {};
      newSectionOrder.forEach((key) => {
        if (sectionTemplates[key]) {
          reorderedTemplates[key] = sectionTemplates[key];
        }
      });
      setSectionTemplates(reorderedTemplates);
    } else if (type === "ITEM") {
      // Reorder items within a section
      const sectionName = source.droppableId;
      const items = Array.from(resumeData[sectionName] || []);
      const [removed] = items.splice(source.index, 1);
      items.splice(destination.index, 0, removed);

      setResumeData((prev) => ({
        ...prev,
        [sectionName]: items,
      }));
    }
  };

  if (!resumeData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <SmartHireLoader />
      </Box>
    );
  }
  

  return (
    <>
      {/* <NewAppBar /> */}
      {/* Commented out - NewAppBar is already rendered by profile.jsx when ResumeEditor is displayed there */}
      {renderLoadingOverlay()}

      {/* Progress indicator for auto-saving */}
      {isAutoSaving && (
        <LinearProgress
          sx={{
            position: "fixed",
            top: 64,
            left: 0,
            right: 0,
            zIndex: 1300,
            bgcolor: "rgba(0,0,0,0.1)",
          }}
        />
      )}

      {/* Error display */}
      <Collapse in={Object.keys(validationErrors).length > 0}>
        <Alert
          severity="error"
          sx={{ mx: 2, mt: 1 }}
          onClose={() => setValidationErrors({})}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {t("validation_errors.fix_errors_below")}
          </Typography>
          {Object.entries(validationErrors).map(([field, error]) => (
            <Typography key={field} variant="body2" sx={{ ml: 2 }}>
              • {error}
            </Typography>
          ))}
        </Alert>
      </Collapse>

      {/* Backup notification */}
      {/* Backup notification - Removed */ }

      {/* Floating action buttons */}
      <Box sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1000 }}>
        <Stack spacing={2}>
          {/* Help button */}
          <Zoom in={!helpMode}>
            <Fab
              color="secondary"
              size="small"
              onClick={() => setHelpMode(true)}
              aria-label={t("ui_text.show_help_aria")}
            >
              <HelpIcon />
            </Fab>
          </Zoom>

          {/* Scroll to top */}
          <Zoom in={showScrollToTop}>
            <Fab
              color="primary"
              size="small"
              onClick={scrollToTop}
              aria-label={t("ui_text.scroll_to_top_aria")}
            >
              <UpIcon />
            </Fab>
          </Zoom>

          {/* Undo/Redo buttons when editing */}
          {isEditing && (
            <>
              <Zoom in={undoStack.length > 0}>
                <Fab
                  size="small"
                  onClick={handleUndo}
                  disabled={undoStack.length === 0}
                  aria-label={t("ui_text.undo_aria")}
                  sx={{ bgcolor: "grey.100" }}
                >
                  <UndoIcon />
                </Fab>
              </Zoom>

              <Zoom in={redoStack.length > 0}>
                <Fab
                  size="small"
                  onClick={handleRedo}
                  disabled={redoStack.length === 0}
                  aria-label={t("ui_text.redo_aria")}
                  sx={{ bgcolor: "grey.100" }}
                >
                  <RedoIcon />
                </Fab>
              </Zoom>
            </>
          )}
        </Stack>
      </Box>
      {isEditing && (
        <Box
          width="100%"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            mb: 2,
            backdropFilter: "saturate(180%) blur(6px)",
            WebkitBackdropFilter: "saturate(180%) blur(6px)",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.75), rgba(255,255,255,0.55))",
            borderBottom: "1px solid #E2E8F0",
            borderRadius: 2,
            px: 2,
            py: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "space-between",
              paddingX: 2,
            }}
          >
            {/* Status indicators */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {renderProgressIndicator(40, 3, "11px", false)}
              {isDirty && (
                <Chip
                  label={t("ui_text.unsaved_changes")}
                  size="small"
                  color="warning"
                  sx={{ fontFamily: "Poppins" }}
                />
              )}

              {isAutoSaving && (
                <Chip
                  label={t("ui_text.auto_saving")}
                  size="small"
                  color="info"
                  sx={{ fontFamily: "Poppins" }}
                />
              )}

              <Typography variant="caption" color="text.secondary">
                {t("ui_text.keyboard_shortcuts_help")}
              </Typography>
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <OutlinedButton
                onClick={cancelEditing}
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: "10px",
                  fontWeight: 600,
                  fontFamily: "Poppins",
                }}
              >
                {t("resume_editor.Cancel")}
              </OutlinedButton>
              <OutlinedButton
                // onClick={saveResumeAsJson}
                onClick={() => {
                  saveResumeAsJson();
                  setIsEditing(false);
                  setEditSnapshot(null);
                }}
                disabled={Object.keys(validationErrors).length > 0 || !emailValidity.available}
                sx={{
                  border: freeTrial >= 3 ? "1px solid #FF0000" : "",
                  py: 1,
                  px: 2,
                  borderRadius: "10px",
                  fontWeight: 600,
                  fontFamily: "Poppins",
                }}
                startIcon={
                  <img src={save_icon} style={{ width: 18, height: 18 }} />
                }
              >
                {t("resume_editor.Save Changes")}
              </OutlinedButton>
            </Box>
          </Box>
        </Box>
      )}
      <Container maxWidth="lg" sx={{ pt: 1, pb: 6, px: { xs: 2, md: 3 } }}>

        {/* Header content moved to portals */}
        {percentageContainer && createPortal(
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {renderProgressIndicator(60, 4, "14px", false)}
            </Box>,
            percentageContainer
        )}

        {buttonsContainer && !isEditing && createPortal(
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <OutlinedButton
                  disabled={!canModifyResume}
                  variant={isEditing ? "outlined" : "contained"}
                  startIcon={
                      <img
                        src={upload}
                        alt="Upload"
                        style={{
                          width: 20,
                          height: 20,
                          marginRight: 8,
                        }}
                      />
                  }
                  sx={{
                    py: 1.5,
                    px: 3,
                    borderRadius: "12px",
                    fontWeight: 600,
                    fontFamily: "Poppins",
                    whiteSpace: "nowrap",
                    boxShadow: 'none'
                  }}
                >
                  {t("resume_editor.Upload resume")}
                </OutlinedButton>
                <OutlinedButton
                  disabled={!canModifyResume}
                  variant={isEditing ? "outlined" : "contained"}
                  startIcon={
                    isEditing ? (
                      <img
                        src={save_icon}
                        style={{
                          width: 20,
                          height: 20,
                          marginRight: 8,
                        }}
                      />
                    ) : (
                      <img
                        src={edit_icon}
                        alt="Edit"
                        style={{
                          width: 20,
                          height: 20,
                          marginRight: 8,
                        }}
                      />
                    )
                  }
                  onClick={() => {
                    if (isEditing) {
                      saveResumeAsJson();
                      setIsEditing(false);
                      setEditSnapshot(null);
                    } else {
                      startEditing();
                    }
                  }}
                  sx={{
                    py: 1.5,
                    px: 3,
                    borderRadius: "12px",
                    fontWeight: 600,
                    fontFamily: "Poppins",
                    whiteSpace: "nowrap",
                    boxShadow: 'none'
                  }}
                >
                  {isEditing
                    ? t("resume_editor.Save Changes")
                    : t("resume_editor.Edit Resume")}
                </OutlinedButton>
                <Button sx={{
                  bgcolor: "transparent",
                  borderRadius: "12px",
                  p: 0,
                  minWidth: 'auto',
                  minHeight: 'auto',
                  width: 40,
                  height: 40,
                  border: '1px solid #E2E8F0'
                }}
                 onClick={handleClick}
                >
                  <img src={three_dots} alt="Menu" />
                </Button>
            </Box>,
            buttonsContainer
        )}

        {/* Original header removed */}


        <BasicInfoSection
          resumeData={resumeData}
          setResumeData={(newData) => {
            if (isEditing) {
              saveToUndoStack();
              setIsDirty(true);
            }
            setResumeData(newData);
            validateResumeData(
              typeof newData === "function" ? newData(resumeData) : newData
            );
          }}
          isEditing={isEditing}
          t={t}
          StyledTextField={StyledTextField}
          validationErrors={validationErrors}
          originalEmail={originalEmail}
          userId={user?.formData?.id}
          setEmailValidity={setEmailValidity}
        />

        {isEditing && (
          <StyledPaper>
            <SectionHeader>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    width: 40,
                    height: 40,
                  }}
                >
                  <SettingsIcon />
                </Avatar>
                <StyledTitle>Section Manager</StyledTitle>
              </Box>
              <OutlinedButton
                onClick={() => openAddDialog("create-section")}
                sx={{
                  py: 1,
                  px: 2.5,
                  borderRadius: "10px",
                  fontWeight: 600,
                  fontFamily: "Poppins",
                }}
              >
                <img
                  src={add_icon}
                  alt="add"
                  style={{ width: 18, height: 18, marginRight: 8 }}
                />
                {t(`resume_editor.New Section`)}
              </OutlinedButton>
            </SectionHeader>

            <Grid container spacing={3}>
              {Object.entries(sectionTypes).map(([type, config]) => (
                <Grid item xs={12} sm={6} md={3} key={type}>
                  <StyledCard>
                    <CardContent sx={{ textAlign: "center", pb: 2 }}>
                      <Typography
                        variant="h3"
                        component="div"
                        mb={2}
                        sx={{ fontSize: "40px" }}
                      >
                        {config.icon}
                      </Typography>
                      <StyledTitle variant="h6" component="div" mb={1}>
                        {config.name}
                      </StyledTitle>
                      <StyledTypography
                        variant="body2"
                        color="text.secondary"
                        mb={2}
                      >
                        {config.description}
                      </StyledTypography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: "center", pt: 0 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setNewItemValue(type);
                          openAddDialog("create-section", "", type);
                        }}
                        sx={{
                          borderRadius: "8px",
                          fontFamily: "Poppins",
                          fontWeight: 500,
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          "&:hover": {
                            borderColor: theme.palette.primary.dark,
                            backgroundColor: "rgba(59, 130, 246, 0.04)",
                          },
                        }}
                      >
                        {t(`resume_editor.Create`)} {config.name}
                      </Button>
                    </CardActions>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          </StyledPaper>
        )}

        <Box sx={{ mt: 4 }}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable
              droppableId="sections"
              type="SECTION"
              isDropDisabled={!isEditing}
            >
              {(provided, snapshot) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    minHeight: "200px",
                    backgroundColor: snapshot.isDraggingOver
                      ? "rgba(59, 130, 246, 0.04)"
                      : "transparent",
                    borderRadius: 2,
                    transition: "background-color 0.2s ease",
                    p: 2,
                  }}
                >
                  {sectionOrder.map((sectionName, index) => {
                    const sectionData = resumeData[sectionName];
                    return renderDraggableSection(
                      sectionName,
                      sectionData,
                      index
                    );
                  })}
                  {provided.placeholder}

                  {Object.entries(sectionTemplates)
                    .filter(
                      ([sectionName]) =>
                        !resumeData[sectionName] &&
                        !sectionOrder.includes(sectionName)
                    )
                    .map(([sectionName], index) =>
                      renderDraggableSection(
                        sectionName,
                        [],
                        sectionOrder.length + index
                      )
                    )}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </Box>

        {renderEnhancedDialog()}

        {/* Help overlay */}
        <Dialog
          open={helpMode}
          onClose={() => setHelpMode(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "20px",
              border: "1.5px solid #D9D9D9",
            },
          }}
        >
          <DialogTitle>
            <Typography
              variant="h5"
              sx={{ fontFamily: "Poppins", fontWeight: 600 }}
            >
              {t("resume_editor.Resume Editor Help")}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontFamily: "Poppins", fontWeight: 600 }}
                >
                  {t("resume_editor.Keyboard Shortcuts")}
                </Typography>
                <Stack spacing={1}>
                  <Typography>
                    <strong>Ctrl+S</strong> - {t("resume_editor.Save changes")}
                  </Typography>
                  <Typography>
                    <strong>Ctrl+E</strong> - {t("resume_editor.Start editing")}
                  </Typography>
                  <Typography>
                    <strong>Ctrl+Z</strong> - {t("resume_editor.Undo")}
                  </Typography>
                  <Typography>
                    <strong>Ctrl+Shift+Z</strong> - {t("resume_editor.Redo")}
                  </Typography>
                  <Typography>
                    <strong>Esc</strong> - {t("resume_editor.Close dialogs")}
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontFamily: "Poppins", fontWeight: 600 }}
                >
                  {t("resume_editor.Features")}
                </Typography>
                <Stack spacing={1}>
                  <Typography>
                    • <strong>{t("Auto-save")}:</strong>{" "}
                    {t(
                      "resume_editor.Your changes are automatically saved every 5 seconds"
                    )}
                  </Typography>
                  <Typography>
                    • <strong>{t("resume_editor.Drag & Drop")}:</strong>{" "}
                    {t(
                      "resume_editor.Reorder sections and items by dragging them"
                    )}
                  </Typography>
                  <Typography>
                    • <strong>{t("resume_editor.Custom Sections")}:</strong>{" "}
                    {t(
                      "resume_editor.Add your own sections with different types"
                    )}
                  </Typography>
                  <Typography>
                    • <strong>{t("resume_editor.Validation")}:</strong>{" "}
                    {t(
                      "resume_editor.Real-time validation ensures your resume is complete"
                    )}
                  </Typography>
                  <Typography>
                    • <strong>{t("resume_editor.Backup")}:</strong>{" "}
                    {t("resume_editor.Automatic backup to prevent data loss")}
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontFamily: "Poppins", fontWeight: 600 }}
                >
                  {t("resume_editor.Section Types")}
                </Typography>
                <Stack spacing={1}>
                  <Typography>
                    • <strong>{t("resume_editor.Simple")}:</strong>{" "}
                    {t("resume_editor.For lists like skills, languages")}
                  </Typography>
                  <Typography>
                    • <strong>{t("resume_editor.Object")}:</strong>{" "}
                    {t(
                      "resume_editor.For detailed items like experience, education"
                    )}
                  </Typography>
                  <Typography>
                    • <strong>{t("resume_editor.Text")}:</strong>{" "}
                    {t("resume_editor.For paragraphs like summary, objectives")}
                  </Typography>
                  <Typography>
                    • <strong>{t("resume_editor.Contact")}:</strong>{" "}
                    {t("resume_editor.For contact information with labels")}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <OutlinedButton onClick={() => setHelpMode(false)}>
              {t("resume_editor.Got it!")}
            </OutlinedButton>
          </DialogActions>
        </Dialog>

        {/* Free Trial Limitation Dialog */}
        <Dialog
          open={showFreeTrialDialog}
          onClose={() => setShowFreeTrialDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "20px",
              border: "1.5px solid #D9D9D9",
            },
          }}
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: "#fff3cd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontSize: "24px" }}>⏳</Typography>
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 600,
                  color: "#856404",
                }}
              >
                {t("free_trial.limit_reached_title")}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3}>
              {freeTrial && (
                <>
                  <Alert severity="info" sx={{ borderRadius: "12px" }}>
                    <Typography sx={{ fontFamily: "Poppins", fontWeight: 500 }}>
                      {freeTrialMessage}
                    </Typography>
                  </Alert>

                  <Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontFamily: "Poppins", fontWeight: 600 }}
                    >
                      {t("free_trial.current_status")}
                    </Typography>
                    <Stack spacing={1}>
                      <Typography sx={{ fontFamily: "Poppins" }}>
                        <strong>{t("free_trial.modifications_made")}</strong>{" "}
                        {freeTrial}/3
                      </Typography>
                      {/* {freeTrial.in_cooldown && freeTrial.remaining_time_formatted && ( */}
                      <Typography
                        sx={{ fontFamily: "Poppins", color: "#d32f2f" }}
                      >
                        <strong>{t("free_trial.cooldown_remaining")}</strong>{" "}
                        {realTimeCountdown || remainingTimeFormatted}
                      </Typography>
                      {/* )} */}
                      {!freeTrial.in_cooldown &&
                        freeTrial.remaining_modifications > 0 && (
                          <Typography
                            sx={{ fontFamily: "Poppins", color: "#2e7d32" }}
                          >
                            <strong>
                              {t("free_trial.remaining_modifications")}
                            </strong>{" "}
                            {freeTrial.remaining_modifications}
                          </Typography>
                        )}
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      backgroundColor: "#f8f9fa",
                      p: 3,
                      borderRadius: "12px",
                    }}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontFamily: "Poppins", fontWeight: 600 }}
                    >
                      {t("free_trial.how_it_works_title")}
                    </Typography>
                    <Stack spacing={1}>
                      <Typography sx={{ fontFamily: "Poppins" }}>
                        • {t("free_trial.modification_limit")}
                      </Typography>
                      <Typography sx={{ fontFamily: "Poppins" }}>
                        • {t("free_trial.cooldown_period")}
                      </Typography>
                      <Typography sx={{ fontFamily: "Poppins" }}>
                        • {t("free_trial.each_modification_includes")}
                      </Typography>
                      <Typography sx={{ fontFamily: "Poppins" }}>
                        • {t("free_trial.cooldown_resets")}
                      </Typography>
                    </Stack>
                  </Box>

                  {freeTrial.in_cooldown && (
                    <Box
                      sx={{
                        backgroundColor: "#fff3cd",
                        p: 3,
                        borderRadius: "12px",
                        border: "1px solid #ffeaa7",
                      }}
                    >
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontFamily: "Poppins",
                          fontWeight: 600,
                          color: "#856404",
                        }}
                      >
                        {t("free_trial.what_can_you_do_title")}
                      </Typography>
                      <Stack spacing={1}>
                        <Typography
                          sx={{ fontFamily: "Poppins", color: "#856404" }}
                        >
                          • {t("free_trial.review_resume")}
                        </Typography>
                        <Typography
                          sx={{ fontFamily: "Poppins", color: "#856404" }}
                        >
                          • {t("free_trial.plan_changes")}
                        </Typography>
                        <Typography
                          sx={{ fontFamily: "Poppins", color: "#856404" }}
                        >
                          • {t("free_trial.export_pdf")}
                        </Typography>
                        <Typography
                          sx={{ fontFamily: "Poppins", color: "#856404" }}
                        >
                          •{" "}
                          {t("free_trial.come_back_in", {
                            time: freeTrial.remaining_time_formatted,
                          })}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <OutlinedButton
              onClick={() => setShowFreeTrialDialog(false)}
              sx={{
                borderRadius: "10px",
                fontFamily: "Poppins",
                fontWeight: 600,
              }}
            >
              {t("free_trial.i_understand")}
            </OutlinedButton>
            {freeTrial && !freeTrial.in_cooldown && (
              <Button
                variant="contained"
                onClick={() => {
                  setShowFreeTrialDialog(false);
                  fetchFreeTrial(userId?.userId); // Refresh the status
                }}
                sx={{
                  borderRadius: "10px",
                  fontFamily: "Poppins",
                  fontWeight: 600,
                  backgroundColor: "#3b82f6",
                }}
              >
                {t("free_trial.check_status_again")}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Password Settings Section - Added at bottom of Resume Editor */}
        <StyledPaper sx={{ mt: 4, mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <StyledTitle>
              {t('profile.Password settings')}
            </StyledTitle>

            <Button
              variant="outlined"
              onClick={() => setIsEditingPassword(true)}
              disabled={isEditingPassword || !has_password}
              sx={{
                width: 135,
                height: 48,
                borderRadius: '10px',
                gap: '10px',
                paddingTop: '14px',
                paddingRight: '20px',
                paddingBottom: '14px',
                paddingLeft: '20px',
                backgroundColor: colors.primary,
                border: 'none',
                "&:hover": {
                  border: "none",
                  backgroundColor: "rgba(26, 35, 126, 0.04)",
                  "& .MuiTypography-root": {
                    color: colors.primary,
                  },
                  "& img": {
                    filter: `brightness(0) saturate(100%) invert(23%) sepia(74%) saturate(1982%) hue-rotate(220deg) brightness(93%) contrast(98%)`
                  }
                },
                "&.Mui-disabled": {
                  bgcolor: "#e0e0e0",
                },
              }}
            >
              <img src={edit_icon} alt="Edit" style={{ width: '16px', height: '16px' }} />
              <Typography
                sx={{
                  paddingLeft: 0,
                  fontFamily: 'Poppins',
                  fontWeight: 400,
                  fontSize: '12px',
                  textTransform: 'none',
                  color: 'white',
                }}
              >
                {t("personalInformation.edit")}
              </Typography>
            </Button>
          </Box>

          {/* Password Form Fields */}
          <Grid
            container
            columnSpacing={5}
            rowSpacing={6}
            sx={{
              pl: 3,
              pr: 3,
            }}
          >
            {/* Current Password */}
            <Grid item xs={12} spacing={5} sx={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <Grid item xs={3}>
                <StyledTypography>
                  {t('profile.Current password')}
                </StyledTypography>
              </Grid>
              <Grid item xs={4}>
                <StyledTextField
                  disabled={!isEditingPassword}
                  fullWidth
                  required
                  id="currentPassword"
                  name="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordValues.currentPassword}
                  onChange={handlePasswordInputChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={toggleCurrentPasswordVisibility}
                          edge="end"
                          disabled={!isEditingPassword}
                          sx={{ marginRight: 1 }}
                        >
                          <img
                            src={showCurrentPassword ? opened_eye_icon : closed_eye_icon}
                            alt={showCurrentPassword ? "Hide password" : "Show password"}
                            style={{
                              width: '15px',
                              height: '15px',
                              filter: !isEditingPassword ? 'grayscale(100%)' : 'none',
                              opacity: !isEditingPassword ? 0.5 : 1
                            }}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* New Password */}
            <Grid item xs={12} spacing={5} sx={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <Grid item xs={3}>
                <StyledTypography>
                  {t('profile.New password')}
                </StyledTypography>
              </Grid>
              <Grid item xs={4}>
                <StyledTextField
                  disabled={!isEditingPassword}
                  fullWidth
                  required
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordValues.newPassword}
                  onChange={handlePasswordInputChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={toggleNewPasswordVisibility}
                          edge="end"
                          disabled={!isEditingPassword}
                          sx={{ marginRight: 1 }}
                        >
                          <img
                            src={showNewPassword ? opened_eye_icon : closed_eye_icon}
                            alt={showNewPassword ? "Hide password" : "Show password"}
                            style={{
                              width: '15px',
                              height: '15px',
                              filter: !isEditingPassword ? 'grayscale(100%)' : 'none',
                              opacity: !isEditingPassword ? 0.5 : 1
                            }}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* Confirm New Password */}
            <Grid item xs={12} spacing={5} sx={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <Grid item xs={3}>
                <StyledTypography>
                  {t('profile.Confirm new password')}
                </StyledTypography>
              </Grid>
              <Grid item xs={4}>
                <StyledTextField
                  disabled={!isEditingPassword}
                  fullWidth
                  required
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordValues.confirmPassword}
                  onChange={handlePasswordInputChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={toggleConfirmPasswordVisibility}
                          edge="end"
                          disabled={!isEditingPassword}
                          sx={{ marginRight: 1 }}
                        >
                          <img
                            src={showConfirmPassword ? opened_eye_icon : closed_eye_icon}
                            alt={showConfirmPassword ? "Hide password" : "Show password"}
                            style={{
                              width: '15px',
                              height: '15px',
                              filter: !isEditingPassword ? 'grayscale(100%)' : 'none',
                              opacity: !isEditingPassword ? 0.5 : 1
                            }}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Update Button */}
          {isEditingPassword ? (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}>
              <NewSearchButton
                variant="contained"
                onClick={handleUpdatePassword}
                disabled={!isEditingPassword || passwordLoading}
              >
                {passwordLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    <span>{t("personalInformation.updating")}</span>
                  </Box>
                ) : (
                  t("personalInformation.update_profile")
                )}
              </NewSearchButton>

              <CancelButton
                variant="contained"
                onClick={handleCancelPassword}
                disabled={!isEditingPassword || passwordLoading}
              >
                {passwordLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    <span>{t("personalInformation.updating")}</span>
                  </Box>
                ) : (
                  <>
                    <img src={cancel_icon} alt="Cancel" />
                    <Typography>
                      Cancel
                    </Typography>
                  </>
                )}
              </CancelButton>
            </Box>
          ) : null}
        </StyledPaper>
      </Container>
      <Menu
        id="three-dots-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "three-dots-button",
        }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      >
        <MenuItem onClick={handlePreview}>
          <ListItemIcon>
            <Preview fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("resume_editor.preview")}</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleDownload}>
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("resume_editor.download")}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ResumeEditor;
